'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FleetSize {
  id: string;
  title: string;
  range: string;
  icon: string;
  description: string;
  benefits: string[];
  cta: string;
}

const FleetSizeSelector = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState<string>('medium');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fleetSizes: FleetSize[] = [
    {
      id: 'small',
      title: 'Small Fleet',
      range: '1-10 Vehicles',
      icon: 'TruckIcon',
      description: 'Perfect for small businesses and startups looking to maximize value on individual vehicle sales.',
      benefits: [
        'Personalized attention for each vehicle',
        'Quick 24-hour valuations',
        'Flexible pickup scheduling'
      ],
      cta: 'Get Individual Valuation'
    },
    {
      id: 'medium',
      title: 'Medium Fleet',
      range: '11-50 Vehicles',
      icon: 'BuildingOfficeIcon',
      description: 'Ideal for growing companies transitioning fleets or upgrading vehicle categories.',
      benefits: [
        'Bulk pricing advantages',
        'Dedicated account manager',
        'Staggered acquisition options'
      ],
      cta: 'Request Fleet Assessment'
    },
    {
      id: 'large',
      title: 'Large Fleet',
      range: '50+ Vehicles',
      icon: 'BuildingOffice2Icon',
      description: 'Enterprise solutions for major fleet operators requiring comprehensive liquidation strategies.',
      benefits: [
        'Custom acquisition programs',
        'Priority processing',
        'Strategic timing consultation'
      ],
      cta: 'Schedule Consultation'
    }
  ];

  const handleFleetSelect = (fleetId: string) => {
    if (!isHydrated) return;
    setSelectedFleet(fleetId);
  };

  const selectedFleetData = fleetSizes.find(f => f.id === selectedFleet) || fleetSizes[1];

  if (!isHydrated) {
    return (
      <section className="bg-background py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Find Your Perfect Solution
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every fleet is unique. Select your fleet size to see tailored solutions and benefits.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {fleetSizes.map((fleet) => (
              <div
                key={fleet.id}
                className="p-6 bg-card rounded-xl border-2 border-border cursor-pointer"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Icon name={fleet.icon as any} size={32} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-1">{fleet.title}</h3>
                    <p className="text-sm text-muted-foreground">{fleet.range}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Find Your Perfect Solution
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every fleet is unique. Select your fleet size to see tailored solutions and benefits.
          </p>
        </div>

        {/* Fleet Size Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {fleetSizes.map((fleet) => (
            <button
              key={fleet.id}
              onClick={() => handleFleetSelect(fleet.id)}
              className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                selectedFleet === fleet.id
                  ? 'bg-primary border-primary shadow-brand'
                  : 'bg-card border-border hover:border-primary/50 hover:shadow-md'
              }`}
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className={`p-4 rounded-full ${
                  selectedFleet === fleet.id ? 'bg-primary-foreground/20' : 'bg-primary/10'
                }`}>
                  <Icon
                    name={fleet.icon as any}
                    size={32}
                    variant="outline"
                    className={selectedFleet === fleet.id ? 'text-primary-foreground' : 'text-primary'}
                  />
                </div>
                <div>
                  <h3 className={`text-xl font-bold mb-1 ${
                    selectedFleet === fleet.id ? 'text-primary-foreground' : 'text-foreground'
                  }`}>
                    {fleet.title}
                  </h3>
                  <p className={`text-sm ${
                    selectedFleet === fleet.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    {fleet.range}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Fleet Details */}
        <div className="bg-card rounded-2xl p-8 md:p-12 shadow-brand">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                  {selectedFleetData.title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  {selectedFleetData.description}
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Key Benefits
                </p>
                {selectedFleetData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground">{benefit}</p>
                  </div>
                ))}
              </div>

              <button className="w-full md:w-auto px-8 py-4 bg-cta text-cta-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-1 shadow-brand flex items-center justify-center space-x-2">
                <span>{selectedFleetData.cta}</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </button>
            </div>

            <div className="bg-primary/5 rounded-xl p-8 border border-primary/10">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="ClockIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-foreground mb-1">Fast Turnaround</h4>
                    <p className="text-sm text-muted-foreground">Get valuations within 24-48 hours and close deals on your timeline.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="CurrencyDollarIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-foreground mb-1">Premium Pricing</h4>
                    <p className="text-sm text-muted-foreground">Receive 15-25% more than typical auction prices with zero hidden fees.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="UserGroupIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-foreground mb-1">Dedicated Support</h4>
                    <p className="text-sm text-muted-foreground">Work with experienced fleet specialists who understand your business needs.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FleetSizeSelector;